import { CalendarDays } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Props = {
  value: string | null | undefined;
  onChange: (isoDate: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  "aria-label"?: string;
};

function isoToDisplay(value: string | null | undefined) {
  const text = String(value || "").slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
}

function displayToIso(value: string) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return "";
  const [, day, month, year] = match;
  const iso = `${year}-${month}-${day}`;
  const date = new Date(`${iso}T00:00:00`);
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== Number(year)
    || date.getMonth() + 1 !== Number(month)
    || date.getDate() !== Number(day)
  ) return "";
  return iso;
}

function normalizeTypedDate(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function DateInput({ value, onChange, min, max, disabled, required, className, "aria-label": ariaLabel }: Props) {
  const [display, setDisplay] = useState(() => isoToDisplay(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplay(isoToDisplay(value));
  }, [value]);

  function commit(nextDisplay: string) {
    if (!nextDisplay) {
      onChange("");
      return;
    }
    const iso = displayToIso(nextDisplay);
    if (!iso) return;
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange(iso);
  }

  function openPicker() {
    const picker = pickerRef.current;
    if (!picker || disabled) return;
    try {
      if (typeof picker.showPicker === "function") picker.showPicker();
      else picker.click();
    } catch {
      picker.focus();
    }
  }

  return <div className={["date-input", className].filter(Boolean).join(" ")}>
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="DD/MM/YYYY"
      aria-label={ariaLabel}
      disabled={disabled}
      required={required}
      value={display}
      maxLength={10}
      onChange={(event) => {
        const next = normalizeTypedDate(event.target.value);
        setDisplay(next);
        commit(next);
      }}
      onBlur={() => {
        const iso = displayToIso(display);
        if (!display) return;
        if (!iso || (min && iso < min) || (max && iso > max)) setDisplay(isoToDisplay(value));
      }}
    />
    <button
      type="button"
      className="date-input-button"
      aria-label={`${ariaLabel || "Fecha"}: abrir calendario`}
      title="Abrir calendario"
      disabled={disabled}
      onClick={openPicker}
    >
      <CalendarDays size={18} aria-hidden="true" />
    </button>
    <input
      ref={pickerRef}
      className="date-input-picker"
      type="date"
      tabIndex={-1}
      min={min}
      max={max}
      disabled={disabled}
      required={required}
      aria-label={`${ariaLabel || "Fecha"}: selector de calendario`}
      value={String(value || "").slice(0, 10)}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>;
}
